import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import { isFileDrag } from '../lib/upload'

interface Props {
  enabled: boolean
  onFiles: (files: File[]) => void
}

/** Freelo #33206294: photos dropped from the computer onto the photo area open the upload dialog with them. */
const PhotoDropZone: React.FC<Props> = ({ enabled, onFiles, children }) => {
  const { t } = useTranslation()
  // dragenter/dragleave fire for every child crossed; the counter keeps the overlay steady.
  const [depth, setDepth] = useState(0)
  if (!enabled) return <>{children}</>

  const accepts = (e: React.DragEvent) => isFileDrag(Array.from(e.dataTransfer.types))

  return (
    <div
      className="relative min-h-[50vh]"
      onDragEnter={(e) => {
        if (!accepts(e)) return
        e.preventDefault()
        setDepth((d) => d + 1)
      }}
      onDragOver={(e) => {
        if (!accepts(e)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }}
      onDragLeave={(e) => {
        if (accepts(e)) setDepth((d) => Math.max(0, d - 1))
      }}
      onDrop={(e) => {
        if (!accepts(e)) return
        e.preventDefault()
        setDepth(0)
        const files = Array.from(e.dataTransfer.files)
        if (files.length) onFiles(files)
      }}
    >
      {children}
      {depth > 0 && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 pt-24">
          <div className="flex items-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-medium shadow-sm">
            <Upload className="h-4 w-4 text-primary" />
            {t('Photos_drop_to_upload')}
          </div>
        </div>
      )}
    </div>
  )
}

export default PhotoDropZone
